<template>
  <div class="row q-pa-sm justify-between items-center horizontal-line">
    <div class="text-caption text-bold">メニューアセット一覧</div>
    <q-btn no-caps class="btn-save q-ml-md" size="sm" label="アセット追加" @click="handleAddMenu" />
  </div>
  <div class="row q-pa-sm justify-center horizontal-line">
    <div class="text-caption">ラベル</div>
  </div>
  <div class="row q-pa-sm horizontal-line items-center" v-for="(menu, index) in value" :key="index">
    <q-img :src="menu.image_url" style="width: 360px" v-if="menu.image_url" />
    <div style="width: 360px; height: 250px; background-color: #e0e0e0" v-else></div>
    <div class="row">
      <q-btn size="sm" flat round icon="edit" @click="handleEditMenu(index)" />
      <app-copy-button>
        <q-menu>
          <q-list style="min-width: 100px">
            <q-item clickable v-close-popup @click="onCopy(index, 'copy')">
              <q-item-section>このリッチメニューに複製</q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="onCopy(index, 'duplicate')">
              <q-item-section>別のリッチメニューに複製</q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </app-copy-button>
      <q-btn size="sm" flat round color="red" icon="mdi-trash-can" @click="handleDeleteMenu(index)" />
      <q-btn size="sm" flat round icon="visibility" @click="onPreview(index)" />
    </div>
    <div class="q-ml-lg" v-if="menu.image_url">
      <span>{{ menu.label }}</span
      ><br />
      <span>{{
        menu.rich_menu_alias_id.length > 4 ? menu.rich_menu_alias_id.slice(0, 4) + '***' : menu.rich_menu_alias_id
      }}</span>
    </div>
    <q-space />
    <q-btn no-caps class="btn-save" label="タップエリア設定" @click="onSettingMenu(index)" v-if="menu.image_url" />
  </div>
  <SettingTappableModal
    v-if="modalTappableAreasVisible"
    :modalVisible="modalTappableAreasVisible"
    v-model="selectedMenu.tappable_area"
    :defaultImage="defaultSize"
    :isInitialMessage="true"
    :isPushMessage="true"
    :isURL="true"
    :isMessage="true"
    :isSwitchMenu="isSwitchMenu"
    :richMenuContent="value"
    :currentMenuId="selectedMenu.rich_menu_alias_id"
    @update:closeModal="onCloseTappableAreasModal"
  />
  <RichMenuAssetModal
    v-if="modalMenuAssetVisible"
    :modalVisible="modalMenuAssetVisible"
    :menuData="selectedMenu"
    :selectedTemplate="selectedTemplate"
    @onSubmitTemplate="onSubmitTemplate"
    @onSubmitMenu="onSubmitMenu"
    @update:closeModal="onCloseMenuAssetModal"
  />
  <CopyMenuModal
    v-if="modalCopyMenuVisible"
    :menuData="selectedMenu"
    :modalVisible="modalCopyMenuVisible"
    :isDraft="isChanged"
    :loading="copyMenuLoading"
    @update:submitMenu="onSubmitDraftMenu"
    @update:refetchMenu="onRefetchMenu"
    @update:closeModal="onCloseCopyMenuModal"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { maska } from 'maska'
import { constant } from '@/utils/constants'
import { IImageUrlSize, IRichMenuContent, IRichMenuTemplateCompact } from '@/utils/types'
import { NumberUtils } from '@/utils/number'
import SettingTappableModal from '@/components/tappable-area/SettingTappableModal.vue'
import RichMenuAssetModal from '@/components/rich-menus/RichMenuAssetModal.vue'
import cloneDeep from 'lodash/cloneDeep'
import { ObjectUtils } from '@/utils/objects'
import CopyMenuModal from '@/components/rich-menus/CopyMenuModal.vue'
import { now } from '@/utils/datetime'
import { UserModel } from '@/models/user-model'

@Options({
  components: {
    CopyMenuModal,
    RichMenuAssetModal,
    SettingTappableModal,
  },
  directives: { maska },
  emits: ['update:modelValue', 'update:submitMenu', 'update:refetchMenu', 'onPreview'],
})
export default class RichMenuSelector extends Vue {
  @Prop({ default: [] })
  modelValue!: IRichMenuContent[]

  selectedMenu!: IRichMenuContent
  defaultSize: IImageUrlSize = {}
  modalTappableAreasVisible = false
  selectedTemplate: IRichMenuTemplateCompact = {}
  modalMenuAssetVisible = false
  originalMenus: IRichMenuContent[] = []
  modalCopyMenuVisible = false
  copyMenuLoading = false

  get userInfo(): UserModel {
    return this.$store.getters.userInfo
  }

  get value() {
    return this.modelValue
  }

  set value(value: IRichMenuContent[]) {
    this.$emit('update:modelValue', value)
  }

  get isSwitchMenu() {
    return this.value.length > 1
  }

  get isChanged() {
    return Object.keys(this.originalMenus).length && ObjectUtils.isDifference(this.value, this.originalMenus)
  }

  handleSelectedMenu() {
    let width = 0
    let height = 0
    if (!this.selectedMenu) {
      return
    }
    if (this.selectedMenu.image_width) {
      width =
        this.selectedMenu.image_width * (constant.RICH_MENU_IMAGE.IMAGE_DEFAULT_SIZE / this.selectedMenu.image_width)
    }
    if (this.selectedMenu.image_height && this.selectedMenu.image_width) {
      height =
        this.selectedMenu.image_height * (constant.RICH_MENU_IMAGE.IMAGE_DEFAULT_SIZE / this.selectedMenu.image_width)
    }

    this.defaultSize = {
      width,
      height,
      image_url: this.selectedMenu.image_url,
    }
  }

  onSettingMenu(index) {
    if (this.value) {
      this.selectedMenu = this.value[index]
      this.handleSelectedMenu()
    }
    this.modalTappableAreasVisible = true
  }

  handleEditMenu(index: number) {
    if (this.value) {
      this.selectedMenu = this.value[index]
      if (this.selectedMenu.selected_template) {
        this.selectedTemplate = this.selectedMenu.selected_template
        this.selectedTemplate.tappable_area = this.selectedMenu.tappable_area
      } else {
        this.selectedTemplate = {}
      }
      this.modalMenuAssetVisible = true
    }
  }

  handleAddMenu(): void {
    const menu: IRichMenuContent = {
      label: '',
      rich_menu_alias_id: NumberUtils.uniqueId(),
      size: {
        width: constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH,
        height: constant.RICH_MENU_IMAGE.MAX_IMAGE_HEIGHT,
      },
      tappable_area: [],
      image_url: '',
      created_at: now(constant.DATETIME_FORMAT),
      created_by: this.userInfo.display_name,
    }
    this.selectedTemplate = {}
    this.selectedMenu = menu
    this.modalMenuAssetVisible = true
  }

  handleDeleteMenu(index: number): void {
    if (this.value) {
      this.value.splice(index, 1)
      this.selectedTemplate = {}
    }
  }

  onCloseTappableAreasModal() {
    this.modalTappableAreasVisible = false
  }

  onCloseMenuAssetModal() {
    this.modalMenuAssetVisible = false
  }

  onCloseCopyMenuModal() {
    this.modalCopyMenuVisible = false
  }

  onSubmitTemplate(data) {
    this.selectedMenu = data
    this.handleSelectedMenu()
    this.modalTappableAreasVisible = true
  }

  onSubmitMenu(data) {
    this.selectedMenu = data
    const index = this.value.findIndex((item) => item.rich_menu_alias_id === this.selectedMenu.rich_menu_alias_id)
    if (index !== -1) {
      this.value[index] = this.selectedMenu
    } else {
      this.value.push(this.selectedMenu)
    }
    this.modalMenuAssetVisible = false
  }

  onPreview(index: number) {
    if (this.value) {
      this.selectedMenu = this.value[index]
      this.$emit('onPreview', this.selectedMenu)
    }
  }

  onCopy(index: number, type: string) {
    console.log('Copying menu:', index, type)
    if (this.value) {
      const menu = cloneDeep(this.value[index])
      menu.rich_menu_alias_id = NumberUtils.uniqueId()
      menu.label = this.value[index].label + ' copy'
      if (type === 'copy') {
        this.value.push(menu)
      } else {
        this.selectedMenu = menu
        this.modalCopyMenuVisible = true
      }
    }
  }

  onSubmitDraftMenu() {
    this.copyMenuLoading = true
    this.$emit('update:submitMenu')
  }

  onRefetchMenu(data) {
    this.$emit('update:refetchMenu', data)
  }

  updateOriginalMenus() {
    this.originalMenus = cloneDeep(this.value)
    this.copyMenuLoading = false
  }

  mounted() {
    setTimeout(() => {
      this.updateOriginalMenus()
    }, 500)
  }
}
</script>
<style lang="scss" scoped>
</style>
